import {
  Button,
  Drawer,
  Dropdown,
  Grid,
  Image,
  Input,
  Menu,
  Skeleton,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AttachmentIcon from "../../../assets/icons/AttachmentIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import DeleteIconRed from "../../../assets/icons/DeleteIconRed";
import DotsIcon from "../../../assets/icons/DotsIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SendIcon from "../../../assets/icons/SendIcon";
import ThreeDotsIcon from "../../../assets/icons/ThreeDotsIcon";
import PLACEHOLDER_IMG from "../../../assets/images/avatar.svg";
import { useSocket } from "../../../contexts/SocketContext";
import {
  useFileUploadMutation,
  useGetEmployeeDetailsQuery,
} from "../../../features/superAdmin/employeeService/employeeManagementService";
import {
  useDeleteSpecificMessageMutation,
  useGetGroupChatDetailsQuery,
  useGetGroupConversationQuery,
  useGetOneToOneConversationQuery,
} from "../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../hooks/useError";
import { formatMessageTime } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { getExtension } from "../../../utils/string";
import Loader from "../../ui/Loader";
import GroupChatInfo from "./GroupChatInfo";
import SingChatInfo from "./SingChatInfo";
const { useBreakpoint } = Grid;

const urlRegex = /((https?:\/\/|www\.)[^\s]+)/gi;

function linkify(text: string) {
  return text.split(urlRegex).map((part, i) => {
    if (!part) return null;

    if (part.match(urlRegex)) {
      const href =
        part.startsWith("http://") || part.startsWith("https://")
          ? part
          : `http://${part}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-white"
        >
          {part}
        </a>
      );
    } else if (!part.startsWith("https://") && !part.startsWith("http://")) {
      return <span key={i}>{part}</span>;
    }
  });
}

export default function ConversationDetail({
  refetch,
}: {
  refetch: () => void;
  topUserId: number;
}) {
  const { id: recipientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  // const [attachment, setAttachment] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<any>([]);

  const [showInfo, setShowInfo] = useState<{
    open: boolean;
    type: string | undefined;
  }>({
    open: false,
    type: undefined,
  });
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [input, setInput] = useState("");

  const auth = useAppSelector((state) => state.auth);

  const handleShowInfo = () => {
    setShowInfo((prev) => ({
      open: !prev?.open,
      type: recipientId === "group" ? "group" : "one-to-one",
    }));
  };

  const {
    socket,
    registerUser,
    registerSuccess,
    sendPrivateMessage,
    sendGroupMessage,
  } = useSocket();

  const scrollToBottom = () => {
    console.log("Scrolling...");
    return messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const skipSingleC =
    !recipientId || recipientId === "group" || !auth?.user?.id;
  const skipGroupC = !recipientId || recipientId !== "group";

  const {
    data: singleCData,
    isError: isSingleCError,
    error: singleCError,
    isLoading: isSingleCLoading,
    refetch: refetchSingleC,
  } = useGetOneToOneConversationQuery(
    {
      id: auth?.user?.id,
      chatId: chatId,
      params: {
        page: 1,
        limit: 100000000000000000,
        search: search,
      },
    },
    {
      skip: skipSingleC,
    },
  );

  const {
    data: groupCData,
    isError: isGroupCError,
    error: groupCError,
    isLoading: isGroupCLoading,
    refetch: refetchGroupC,
  } = useGetGroupConversationQuery(
    {
      chatId: chatId,
      params: {
        page: 1,
        limit: 100000000000000000,
        search: search,
      },
    },
    {
      skip: skipGroupC,
    },
  );

  const {
    data: recipientData,
    isError,
    error,
  } = useGetEmployeeDetailsQuery(recipientId || "", {
    skip: skipSingleC,
  });

  const {
    data: groupChatDetails,
    isError: isGroupChatError,
    error: groupChatError,
  } = useGetGroupChatDetailsQuery(chatId || "", {
    skip: skipGroupC,
  });

  const [
    fileUpload,
    {
      error: fileUploadError,
      isError: fileUploadIsError,
      isLoading: isFileUploading,
    },
  ] = useFileUploadMutation();
  const [
    deleteSpecificMessage,
    { isSuccess: isDSMSuccess, isError: isDMSError, error: DSMError },
  ] = useDeleteSpecificMessageMutation();

  useError(fileUploadIsError, fileUploadError, "Upload photo fail");
  useError(isError, error, "Get recipient data failed");
  useError(isSingleCError, singleCError, "Get conversation failed");
  useError(isGroupCError, groupCError, "Get group conversation failed");
  useError(isGroupChatError, groupChatError, "Get group chat info failed");
  useError(isDMSError, DSMError, "Message delete failed");

  useEffect(() => {
    if (singleCData) {
      setMessages(singleCData?.data.messages);
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    }
  }, [singleCData]);

  useEffect(() => {
    if (groupCData) {
      setMessages(groupCData?.data.messages);
      // scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    }
  }, [groupCData]);

  useEffect(() => {
    if (recipientId || chatId) {
      setShowInfo({
        open: false,
        type: undefined,
      });
    }
  }, [chatId, recipientId]);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    try {
      if (true) {
        if (auth?.user?.id) {
          registerUser(auth?.user?.id);

          socket?.on("registerSuccess", () => {
            registerSuccess(Number(auth?.user?.id), Number(recipientId));
          });
        }

        socket?.on("one-to-one-connected", (payload: any) => {
          let basePath = "/user/communication/message/";
          if (auth?.role === "super_admin" || auth?.role === "Admin") {
            basePath = "/admin/communication/message/";
          }
          navigate(
            basePath + payload?.connectedTo?.id + "?chatId=" + payload?.chatId,
          );
        });

        socket?.on("messageReceive", (data: any) => {
          if (Number(recipientId) === Number(data?.sender?.id)) {
            setMessages((prev: any) => [data, ...prev]);
          }
          if (!skipSingleC && refetchSingleC) {
            try {
              refetchSingleC();
              scrollToBottom();
            } catch (error) {}
          }
          if (auth?.user?.id && refetch) {
            refetch();
          }

          scrollToBottom();
        });

        return () => {
          socket?.off("registerSuccess");
        };
      }
    } catch (error) {
      console.log(error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.id, recipientId]);

  useEffect(() => {
    if (recipientId === "group") {
      const groupListener = (data: any) => {
        setMessages((prev: any) => [data, ...prev]);
        scrollToBottom();
        if (!skipGroupC && refetchGroupC) {
          try {
            refetchGroupC();
          } catch (error) {}
        }
      };

      socket?.on("received_group_message", groupListener);

      // return () => {
      //   socket?.off("received_group_message", groupListener);
      // };
    }
  }, [socket, recipientId, refetchGroupC, skipGroupC]);

  useEffect(() => {
    if (chatId && auth?.user?.id) {
      socket?.emit("reset_unseen_messages", {
        chatId,
        userId: Number(auth?.user?.id),
      });
      const handleUnseenReset = () => {
        // Only call refetch if it exists
        if (refetch) {
          try {
            refetch();
          } catch (err) {
            // console.warn("Query not ready to refetch", err);
          }
        }
      };

      socket?.on("unseenMessagesReset", handleUnseenReset);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, auth?.user?.id]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const maxFileSize = 32 * 1024 * 1024;

    for (const file of files) {
      if (file.size > maxFileSize) {
        toast("error", `"${file.name}" exceeds max file size of 32MB.`);
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);

      try {
        const res: any = await fileUpload(formData).unwrap();
        if (res?.Location) {
          setAttachments((prev) => [...prev, res.Location]);
        }
      } catch (err) {
        console.error("Image upload failed", err);
      }
    }

    e.target.value = "";
  };

  const isImageOrVideoType = (url: string) => {
    const type = getExtension(url);
    return [
      "png",
      "jpg",
      "jpeg",
      "gif",
      "webp",
      "bmp",
      "svg",
      "mp4",
      "mov",
      "avi",
      "wmv",
      "flv",
      "mkv",
      "webm",
    ].includes(type);
  };

  const handleSend = () => {
    const formattedAttachments =
      attachments.length > 0
        ? attachments.map((link) => ({
            type: isImageOrVideoType(link) ? "media" : "document",
            link,
          }))
        : undefined;

    if (recipientId === "group") {
      sendGroupMessage(
        Number(auth?.user?.id),
        Number(chatId),
        input.trim(),
        formattedAttachments,
      );
      setTimeout(() => {
        if (!skipGroupC && refetchGroupC) {
          try {
            refetchGroupC();
          } catch (error) {}
        }
        if (auth?.user?.id && refetch) {
          refetch();
        }
      }, 100);
    } else {
      sendPrivateMessage(
        Number(auth?.user?.id),
        Number(recipientId),
        input.trim(),
        formattedAttachments,
      );
      if (!skipSingleC && refetchSingleC) {
        try {
          refetchSingleC();
          scrollToBottom();
        } catch (error) {}
      }
      if (auth?.user?.id && refetch) {
        refetch();
      }
    }
    setInput("");
    setAttachments([]);
  };

  useEffect(() => {
    if (recipientId || chatId) {
      requestAnimationFrame(scrollToBottom);
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [chatId, recipientId]);

  useEffect(() => {
    if (isDSMSuccess) {
      toast("success", "Message deleted successfully!");
    }
  }, [isDSMSuccess]);

  const userInfo = {
    name: recipientData?.data?.officialInfo?.name,
    img: recipientData?.data?.officialInfo?.pro_pic,
    id: String(auth?.user?.id),
  };

  return (
    <div
      className={`grid ${
        isMobile ? "" : "grid-cols-5 gap-4 border !border-[#cccccc] rounded-md"
      }`}
    >
      <div
        className={`bg-white p-4 rounded-md relative ${
          isMobile ? "" : showInfo?.open ? "col-span-3" : "col-span-5"
        }`}
      >
        <div className="text-[20px] font-semibold flex justify-between items-center border-b !border-b-[#cccccc] pb-2 ">
          <span>
            {recipientId === "group"
              ? groupChatDetails?.data?.group_name
              : recipientData?.data?.officialInfo?.name || ""}
          </span>
          <div className="flex justify-end items-center">
            <span>
              <Input
                placeholder="Search"
                prefix={<SearchIcon />}
                onChange={(e) => setSearch?.(e.target.value)}
              />
            </span>
            {!showInfo.open && (
              <span className="cursor-pointer p-2" onClick={handleShowInfo}>
                <ThreeDotsIcon />
              </span>
            )}
          </div>
        </div>
        <div
          className={`overflow-y-auto message-body p-2 ${
            !!(attachments?.length || isFileUploading)
              ? "h-[calc(100vh-25.375rem)]"
              : "h-[calc(100vh-18rem)]"
          }`}
        >
          {isSingleCLoading || isGroupCLoading ? (
            <Loader />
          ) : (
            [...messages]?.map((msg: any, index: any) => {
              return (
                <div key={index}>
                  <div
                    className={`flex ${
                      msg?.sender?.id === auth.user?.id
                        ? "justify-end"
                        : "justify-start"
                    } my-2`}
                  >
                    <div className="max-w-[70%] mt-2 flex items-start gap-3">
                      {msg?.sender?.id !== auth.user?.id ? (
                        <img
                          className="size-10 object-cover rounded-full"
                          src={msg?.sender?.pro_pic || PLACEHOLDER_IMG}
                          alt=""
                        />
                      ) : null}

                      <div className="flex items-center gap-3 group">
                        {msg?.sender?.id === auth.user?.id && (
                          <div className="hidden group-hover:block">
                            {msg?.message?.is_deleted === false && (
                              <Dropdown
                                overlay={
                                  <Menu
                                    items={[
                                      {
                                        key: "delete",
                                        label: (
                                          <div className="flex items-center gap-2 text-[#3D4A5C]">
                                            <DeleteIconRed
                                              pathClassName={"stroke-[#3D4A5C]"}
                                            />
                                            Delete
                                          </div>
                                        ),
                                      },
                                    ]}
                                    onClick={({ key }) => {
                                      if (key === "delete") {
                                        const newData = {
                                          userId: auth.user?.id,
                                        };
                                        deleteSpecificMessage({
                                          id: msg?.id,
                                          data: newData,
                                        });
                                      }
                                    }}
                                  />
                                }
                                trigger={["click"]}
                              >
                                <div className="cursor-pointer">
                                  <DotsIcon />
                                </div>
                              </Dropdown>
                            )}
                          </div>
                        )}
                        <div>
                          {msg?.message?.attachments?.map((item: any) => {
                            if (item?.type === "media") {
                              return (
                                <div className="pl-3 overflow-hidden">
                                  <Image
                                    className="mt-1.5 overflow-hidden"
                                    src={item?.link}
                                    style={{
                                      overflow: "hidden",
                                    }}
                                    width={100}
                                    height={100}
                                  />
                                </div>
                              );
                            }

                            return null;
                          })}

                          {msg?.message?.is_deleted === true ? (
                            <div
                              className={`group relative inline-block max-w-[100%] w-full p-3 rounded-lg pl-3 ${
                                msg?.sender?.id === auth.user?.id
                                  ? "bg-[#23807E] text-white"
                                  : "bg-[#F0F3F9] text-[#2D3643]"
                              }`}
                            >
                              <div className="text-sm italic">
                                {msg?.sender?.id === auth.user?.id ? (
                                  <span className="text-white">
                                    You deleted this message
                                  </span>
                                ) : (
                                  <div className="text-[#2D3643]">
                                    This message was deleted
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : null}
                          {msg?.message?.is_deleted !== true &&
                          msg?.message?.content ? (
                            <div
                              className={`group relative inline-block max-w-[100%] w-full p-3 rounded-lg pl-3 ${
                                msg?.sender?.id === auth.user?.id
                                  ? "bg-[#23807E] text-white"
                                  : "bg-[#F0F3F9] text-[#2D3643]"
                              }`}
                            >
                              <div>
                                {!!(msg?.sender?.id !== auth.user?.id) && (
                                  <span className="block text-[#23807E]">
                                    {msg?.sender?.name}
                                  </span>
                                )}
                                <span className="block whitespace-pre-wrap">
                                  {linkify(msg?.message?.content)}
                                </span>
                              </div>
                            </div>
                          ) : null}

                          <div
                            className={`text-[11px] text-[#5E718D] mt-0.5 ${
                              !!(msg?.sender?.id === auth.user?.id) &&
                              "text-right"
                            }`}
                          >
                            {msg?.sender?.id === auth.user?.id &&
                              (msg?.message?.status === "SENT"
                                ? "Sent"
                                : "Sending")}{" "}
                            {formatMessageTime(msg?.message?.createdAt)}
                          </div>
                        </div>
                      </div>
                      {msg?.sender?.id === auth.user?.id ? (
                        <img
                          className="size-10 object-cover rounded-full"
                          src={msg?.sender?.pro_pic || PLACEHOLDER_IMG}
                          alt=""
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef}></div>
        </div>

        <div className="mt-4 ">
          {isFileUploading ? (
            <Skeleton.Image className="h-[114px]" active={true} />
          ) : attachments.length > 0 ? (
            <div className="mb-1 px-3 flex gap-3 flex-wrap">
              {attachments.map((file, index) => (
                <div className="w-[150px] relative" key={index}>
                  <div
                    className="absolute top-5 right-3 z-10 cursor-pointer"
                    onClick={() =>
                      setAttachments((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <CloseIcon />
                  </div>
                  <Image
                    width={150}
                    height={100}
                    src={file}
                    style={{ objectFit: "cover" }}
                    rootClassName="rounded-md"
                    wrapperClassName="mt-2 rounded-md"
                    className="rounded-md object-cover border border-light border-solid"
                  />
                </div>
              ))}
            </div>
          ) : null}

          {/* <div className="flex items-center gap-2 pt-2">
            <Input
              prefix={
                <label htmlFor="attachment">
                  <input
                    type="file"
                    id="attachment"
                    className="hidden"
                    multiple
                    onChange={handleImageChange}
                    accept="image/*"
                  />
                  <AttachmentIcon />
                </label>
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSend();
                }
              }}
              type="text"
              placeholder="Type a message"
              className="border-[#D7DFE9] h-12"
              // suffix={<MicrophoneIcon />}
            />
            <Button
              className="!bg-[#FE7235] !hover:bg-[#FE7235] border-none h-12 px-4"
              onClick={handleSend}
              disabled={(!input && !attachments) || isFileUploading}
            >
              <SendIcon />
            </Button>
          </div> */}
          <div className="flex items-end gap-2 pt-2">
            <div className="flex-1 flex items-center border border-[#D7DFE9] rounded-lg px-2 py-1 focus-within:ring-2 min-h-12">
              <label htmlFor="attachment" className="cursor-pointer pb-1">
                <input
                  multiple
                  type="file"
                  id="attachment"
                  className="hidden"
                  onChange={handleImageChange}
                  accept="image/*"
                />
                <AttachmentIcon />
              </label>

              <textarea
                value={input}
                // onChange={(e) => setInput(e.target.value)}
                onChange={(e) => {
                  setInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Type a message..."
                className="flex-1 resize-none bg-transparent outline-none px-2 py-1 text-sm placeholder:text-gray-400"
                rows={2}
              />
            </div>

            <Button
              className="!bg-[#FE7235] !hover:bg-[#FE7235] border-none h-12 px-4 rounded-lg"
              onClick={handleSend}
              disabled={(!input && !attachments) || isFileUploading}
            >
              <SendIcon />
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Info */}
      {isMobile ? (
        <Drawer
          placement="right"
          closable={true}
          onClose={handleShowInfo}
          open={showInfo?.open}
          width={300}
        >
          {showInfo?.type === "group" ? (
            <GroupChatInfo
              setShowInfo={setShowInfo}
              chatDetails={groupChatDetails}
            />
          ) : (
            <SingChatInfo setShowInfo={setShowInfo} chatDetails={userInfo} />
          )}
        </Drawer>
      ) : (
        <div
          className={`${
            showInfo.open ? "col-span-2" : "hidden"
          } bg-white p-4 h-[calc(100vh-8.8rem)] overflow-hidden`}
        >
          {showInfo?.type === "group" ? (
            <GroupChatInfo
              setShowInfo={setShowInfo}
              chatDetails={groupChatDetails}
            />
          ) : (
            <SingChatInfo setShowInfo={setShowInfo} chatDetails={userInfo} />
          )}
        </div>
      )}
    </div>
  );
}
